type Translator = (key: string, options?: Record<string, any>) => any;

const LEGACY_TYPE_TO_CONTENT: Record<string, string> = {
  collection_overdue: 'COLLECTION_OVERDUE',
  handover_delayed: 'HANDOVER_DELAYED',
  rental_ending: 'RENTAL_ENDING',
  collection_upcoming: 'COLLECTION_UPCOMING',
};

const ENGLISH_TITLE_TO_CONTENT: Record<string, string> = {
  '🆕 New Booking': 'NEW_BOOKING',
  '✅ Booking Confirmed': 'BOOKING_CONFIRMED',
  '❌ Booking Cancelled': 'BOOKING_CANCELLED',
  '📅 Booking Extended': 'BOOKING_EXTENDED',
  '🚗 Vehicle Handover': 'VEHICLE_HANDOVER',
  '📦 Vehicle Returned': 'VEHICLE_RETURN',
  '⏰ Rental Ending Soon': 'RENTAL_ENDING',
  '⚠️ Handover Delayed': 'HANDOVER_DELAYED',
  '⚠️ Overdue Booking': 'OVERDUE_ALERT',
  'Overdue Vehicle Collection': 'COLLECTION_OVERDUE',
  '⏳ Upcoming Vehicle Collection': 'COLLECTION_UPCOMING',
  '📊 Daily Booking Summary': 'DAILY_SUMMARY',
  '🛢️ Oil Change Upcoming': 'VEHICLE_OIL_CHANGE',
  '🔧 Maintenance Due Soon': 'VEHICLE_MAINTENANCE_SERVICE',
  'Insurance Expired': 'VEHICLE_INSURANCE_EXPIRED',
  'Insurance Expiry Upcoming': 'VEHICLE_INSURANCE_UPCOMING',
  '🎉 Agency Approved!': 'AGENCY_APPROVED',
  '❌ Application Rejected': 'AGENCY_REJECTED',
  'ℹ️ Additional Information Required': 'INFO_REQUESTED',
  '🚫 Agency Suspended': 'AGENCY_SUSPENDED',
  '👀 Under Review': 'UNDER_REVIEW',
  '📝 Profile Incomplete': 'PROFILE_INCOMPLETE',
  '📋 Basic Information Required': 'BASIC_INFO_REQUIRED',
  '✅ Bookings Enabled': 'BOOKINGS_ENABLED',
  '⏸️ Bookings Paused': 'BOOKINGS_PAUSED',
};

const BACKEND_TYPE_ALIAS: Record<string, string> = {
  'daily-summary': 'DAILY_SUMMARY',
};

const firstMatch = (re: RegExp, s: string): string | undefined => {
  const match = (s || '').match(re);
  return match ? match[1] : undefined;
};

export const resolveNotificationContentType = (notification: any): string | null => {
  let key = notification.contentType
    || LEGACY_TYPE_TO_CONTENT[notification.type]
    || ENGLISH_TITLE_TO_CONTENT[notification.title];

  if (!key) return null;

  key = BACKEND_TYPE_ALIAS[key] || key;

  if (key === 'VEHICLE_MAINTENANCE') {
    key = /oil change/i.test(notification.message || '') ? 'VEHICLE_OIL_CHANGE' : 'VEHICLE_MAINTENANCE_SERVICE';
  }

  if (key === 'VEHICLE_INSURANCE') {
    key = /expired/i.test(notification.message || '') ? 'VEHICLE_INSURANCE_EXPIRED' : 'VEHICLE_INSURANCE_UPCOMING';
  }

  return key;
};

export const translateNotificationContent = (
  notification: any,
  t: Translator,
): { title: string; message: string } => {
  const type = resolveNotificationContentType(notification);
  if (!type) return { title: notification.title, message: notification.message };

  const base = `notifications.content.${type}`;
  const titleKey = `${base}.title`;
  const translatedTitle = t(titleKey);
  const title = translatedTitle === titleKey ? notification.title : translatedTitle;

  const metadata = notification.metadata || {};
  const msg: string = notification.message || '';

  let vehicle: string = metadata.vehicle || '';
  if (!vehicle) {
    if (type === 'VEHICLE_OIL_CHANGE') vehicle = msg.split(' oil change')[0].trim();
    else if (type === 'VEHICLE_MAINTENANCE_SERVICE') vehicle = msg.split(/\s+[–-]\s+service/)[0].trim();
    else if (type === 'VEHICLE_INSURANCE_EXPIRED' || type === 'VEHICLE_INSURANCE_UPCOMING') vehicle = msg.split(' insurance')[0].trim();
    else vehicle = firstMatch(/\(([^)]+)\)/, msg) || '';
  }

  const vars: Record<string, any> = {
    bookingNumber: notification.bookingNumber || metadata.bookingNumber || '',
    vehicle,
    amount: metadata.paymentAmount ?? metadata.additionalCharges ?? firstMatch(/(?:payment of|charges:)\s*([\d.]+)\s*TND/i, msg) ?? 0,
    reason: metadata.reason || firstMatch(/cancelled:\s*([\s\S]+)$/i, msg) || t('notifications.noReason', { defaultValue: 'No reason provided' }),
    date: metadata.newEndDate ? new Date(metadata.newEndDate).toLocaleDateString() : (firstMatch(/extended to\s*(.+)$/i, msg) || ''),
    hours: metadata.hoursOverdue ?? metadata.hoursDelayed ?? (metadata.minutesRemaining != null ? Math.floor(metadata.minutesRemaining / 60) : undefined)
      ?? firstMatch(/(\d+)h overdue/i, msg) ?? firstMatch(/ends in (\d+) hour/i, msg) ?? 0,
    balance: metadata.balance ?? firstMatch(/Balance due:\s*([\d.]+)\s*TND/i, msg) ?? 0,
    days: firstMatch(/(?:due in|expires in|expired)\s+(\d+)\s+day/i, msg) ?? 0,
    total: firstMatch(/^(\d+) bookings/i, msg) ?? 0,
    revenue: firstMatch(/Revenue:\s*([\d.]+)\s*TND/i, msg) ?? 0,
  };

  let messageKey = `${base}.message`;
  if (type === 'VEHICLE_RETURN' && ((metadata.additionalCharges ?? 0) > 0 || /Additional charges/i.test(msg))) {
    messageKey = `${base}.messageWithCharges`;
  } else if (type === 'COLLECTION_UPCOMING' && ((metadata.balance ?? 0) > 0 || /Balance due/i.test(msg))) {
    messageKey = `${base}.messageWithBalance`;
  }

  const translatedMessage = t(messageKey, vars) as string;
  const message = translatedMessage === messageKey ? notification.message : translatedMessage;

  return { title, message };
};
